<template>
  <div class="user-register-page">
    <nav class="bread-nav" aria-label="You are here:" role="navigation">
      <ul class="breadcrumbs">
        <li><router-link :to="{name:'Index'}">ホーム</router-link></li>
        <li>
          <span class="show-for-sr">Current: </span> {{ title }}
        </li>
      </ul>
    </nav>

    <!-- ログインフォーム -->
    <div class="register-form" v-if="!isLogoutProgress">
      <h1>{{ title }}</h1>

      <div v-show="error_message" class="callout alert">
        <p>{{ error_message }}</p>
      </div>

      <form @submit.prevent="login">
        <label>メールアドレス
          <input type="email" placeholder="メールアドレス" v-model="mail" required>
        </label>

        <label>パスワード
          <input type="password" v-model="password" required>
        </label>

        <button class="button" type="submit">ログイン</button>
      </form>

      <p>
        <router-link :to="{name:'UserForgetPassword'}">▶︎パスワードを忘れた方はこちら</router-link><br />
        <router-link :to="{name:'UserRegister'}">▶︎新規登録はこちら</router-link>
      </p>
    </div>

    <!-- ログアウト実行中の簡易画面 -->
    <div class="register-form" v-else>
      <h1>ログアウトしています…</h1>
    </div>
  </div>
</template>

<script>
import pianoSend from '../mixins/PrefsMixin'

export default {
  name: 'UserLogin',
  mixins: [pianoSend],

  data () {
    return {
      mail: '',
      password: '',
      title: 'ログイン',
      error_message: '',
      isLogoutProgress: false
    }
  },

  head: function () {
    // 既存コードの書式に合わせています
    return {
      title: function () {
        return {
          inner: this.$data.title,
          separator: ' - ',
          complement: 'BOATNAVI報知'
        }
      }
    }
  },

  mounted () {
    this.$emit('updateHead')
  },

  created () {
    this.$emit('updateHead')

    const q = this.$route.query || {}
    // /login?logout=1 [&return=/path] で来たら即ログアウト
    if (q.logout === '1' || q.logout === 1 || q.logout === true || q.logout === 'true') {
      this.isLogoutProgress = true
      this.title = 'ログアウト'
      this.pianoSend('ログアウト')
      this.handleLogoutShortcut()
      return
    }

    this.pianoSend('ログイン')
  },

  watch: {
    '$route.query.logout' (v) {
      if (v && !this.isLogoutProgress) {
        this.isLogoutProgress = true
        this.title = 'ログアウト'
        this.pianoSend('ログアウト')
        this.handleLogoutShortcut()
      }
    }
  },

  methods: {
    // Nuxt 側パスかどうか（Nuxt がハンドリングする先はハード遷移にする）
    isNuxtPath (p) {
      return typeof p === 'string' && /^\/(articles|news-api)(\/|$)/.test(p)
    },

    // ===== ログイン処理 =====
    async login () {
      const loader = this.$loading.show({})
      try {
        await this.$cognito.login(this.mail, this.password)

        // プロフィール取得 → クッキー発行（Nuxt ヘッダー用の保険）
        const attrs = await this.$cognito.getAttribute().catch(() => [])
        const map = {}
        if (Array.isArray(attrs)) {
          for (const a of attrs) {
            if (a && a.getName && a.getValue) map[a.getName()] = a.getValue()
          }
        }
        const user = {
          sub: map.sub,
          email: map.email,
          email_verified: map.email_verified === 'true' || map.email_verified === true,
          nickname: map.nickname || map.name || (map.email ? String(map.email).split('@')[0] : ''),
          name: map.name || map.email
        }
        try { this.$store.commit('setUser', user) } catch (e) { /* noop */ }
        this.setUserCookie(user)

        // 遷移先は redirect > return > /
        const q = this.$route.query || {}
        const next =
          (typeof q.redirect === 'string' && q.redirect.startsWith('/')) ? q.redirect
            : (typeof q.return === 'string' && q.return.startsWith('/')) ? q.return
              : '/'

        loader.hide()

        // Nuxt 側パスならハード遷移（CloudFront 経由で Nuxt/Lambda に行かせる）
        if (this.isNuxtPath(next)) {
          const url = new URL(next, window.location.origin).toString()
          window.location.assign(url)
        } else {
          this.$router.replace(next)
        }
      } catch (err) {
        loader.hide()
        this.error_message =
          (err && err.name === 'NotAuthorizedException')
            ? 'メールアドレスとパスワードが一致しません。'
            : 'ログインを失敗しました。'
      }
    },

    // ===== /login?logout=1 ショートカット =====
    handleLogoutShortcut () {
      const q = this.$route.query || {}
      const ret = (typeof q.return === 'string' && q.return.startsWith('/')) ? q.return : '/'

      try { this.$cognito.logout() } catch (e) { /* noop */ }
      try { this.$store.commit('setUser', null) } catch (e) { /* noop */ }

      this.nukeCookie('__bn_user_json')
      this.nukeCookie('__bn_auth')
      this.nukeCookie('bn_user')
      this.nukeCookie('bn_auth')

      // Nuxt 側に戻るときは必ずハード遷移
      const url = new URL(ret, window.location.origin).toString()
      window.location.assign(url)
    },

    // ===== クッキー発行（Nuxt 側ヘッダー用） =====
    setUserCookie (userObj) {
      try {
        const raw = encodeURIComponent(JSON.stringify(userObj || {}))
        const exp = new Date(Date.now() + 30 * 24 * 3600 * 1000).toUTCString() // 30日
        const base = `__bn_user_json=${raw}; Expires=${exp}; Path=/; SameSite=Lax`
        document.cookie = base
      } catch (e) { /* noop */ }
    },

    // ===== クッキー削除（複数ドメイン試行） =====
    nukeCookie (name) {
      const kill = (domain) =>
        `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/;${domain ? ` Domain=${domain};` : ''} SameSite=Lax`
      try {
        document.cookie = kill('')
        const host = location.hostname
        const parts = host.split('.')
        if (parts.length >= 2) document.cookie = kill('.' + parts.slice(-2).join('.'))
        if (parts.length >= 3) document.cookie = kill('.' + parts.slice(-3).join('.'))
      } catch (e) { /* noop */ }
    }
  }
}
</script>

<style scoped>
.register-form{
  max-width:600px;
  margin:40px auto;
  padding:20px;
}
</style>